import React, { useState } from "react";
import { Checkbox, Divider, Input } from "@mui/material";
import { ModalWrapper } from "../../../../../../shared/components/ModalWrapper";
import { User } from "lucide-react";
import CommonButtons from "src/shared/components/CommonButtons";
import { Order } from "src/shared/services/interfacev2";
import { splitDateTime } from "src/shared/components/dateTime";
import { useFormatAmount } from "../../../home/hooks/useFormatAmount";

interface Props {
  glowery: boolean;
  orderback: () => void;
  getorder: Order | undefined;
  roundValue: (val: number) => number;
  cashoutstanging: number;
  totaloutstandingboc: number;
   handleConfirm: (method: string) => Promise<void>;
  payimg: string;
  money: string;
  inputValue: number | string;
  handleclick: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBlur: () => void;
  Outamount: () => number | undefined;
  formatNumber: (val: number) => string;
  formatNumbers: (val: number) => string;
  formattedBalance: string;
  outstandingvalue: () => void;
  setglowery: (value: boolean) => void;
  deliveryFeeEnabled: boolean;
  paymenytMethod: string;
}

const CashPaymentSummaryModal: React.FC<Props> = ({ glowery, orderback, getorder, roundValue, cashoutstanging, handleConfirm, inputValue, handleclick, handleBlur, Outamount, formatNumber, formatNumbers, formattedBalance, outstandingvalue, setglowery, deliveryFeeEnabled, paymenytMethod }) => {
  const [isLoading, setIsLoading] = useState(false);
  const formatAmount = useFormatAmount();

  if (!glowery) return null;

  const handleComplete = async () => {
    if (isLoading) return; // Prevent double submission
    
    setIsLoading(true);
    try {
      await handleConfirm(paymenytMethod);
      // Success - modal will close or update via parent
    } catch (error) {
      console.error("Payment failed:", error);
      // Optionally show error toast here
    } finally {
      setIsLoading(false);
    }
  }

  const { date: orderDate, time: orderTime } = splitDateTime(getorder?.orderDetails[0].orderedOn.$date ?? "");

  const desableBtn = isLoading || !inputValue || Number(inputValue) <= 0 || Number(formattedBalance) < 0;
  return (
    <ModalWrapper title="Payment Summary" showBackButton onBack={() => setglowery(false)} onClose={() => setglowery(false)} width="max-w-lg w-full" className="min-w-[400px] text-center">
      <div className="text-left">
        {/* Customer Section */}
        <div className="flex items-center gap-3 bg-gray-100 p-3 px-5 mb-3 rounded-md">
          <div className="w-10 h-10 flex items-center justify-center bg-gray-200 rounded-full">
            <User className="text-gray-600" size={22} />
          </div>
          <div className="flex flex-col text-sm">
            <span className="font-semibold">Customer: {getorder?.customerDetails?.firstName || "N/A"}</span>
            <span className="text-gray-600">Payment: {paymenytMethod}</span>
            <span className="text-gray-500 text-xs">
              Date: {orderDate} | {orderTime}
            </span>
          </div>
        </div>

        {/* Order Summary */}
        <div className="mb-4">
          <h3 className="font-semibold text-gray-700 mb-1">Order Summary</h3>
          <Divider className="mb-2" />

          {/* Outstanding balance toggle */}
          <div className="flex items-center justify-between text-sm mb-1">
            <div className="flex items-center gap-0">
              <Checkbox onClick={outstandingvalue} size="small" />
              <span className="font-medium">Outstanding Balance</span>
            </div>
            <span className="font-medium">{formatNumber(cashoutstanging)} QAR</span>
          </div>

          {/* Summary rows */}
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="px-10">Order Amount</span>
              <span className="font-medium">{formatAmount(Number(getorder?.orders[0].orderAmount))} QAR</span>
            </div>

            <div className="flex justify-between">
              <span className="px-10">Discount</span>
              <span>- {formatAmount(Number(getorder?.orders[0].discount))} QAR</span>
            </div>

            {deliveryFeeEnabled && (
              <div className="flex justify-between">
                <span className="px-10">Delivery Fee</span>
                <span className="font-medium">
                  {formatAmount(roundValue(Number(getorder?.orders[0].deliveryFee)))} QAR
                </span>
              </div>
            )}

            <div className="flex justify-between">
              <span className="px-10">Prev Received Amount</span>
              <span>{formatAmount(Number(getorder?.paymentHistory[0].receivedAmount))} QAR</span>
            </div>

            <div className="flex justify-between">
              <span className="px-10">Final Amount</span>
              <span>{formatNumbers(Outamount() ?? 0)} QAR</span>
            </div>
          </div>
        </div>

        {/* Payment Status */}
        <div className="mb-3">
          <h3 className="font-semibold text-gray-700 mb-1">Payment Status</h3>
          <Divider className="mb-2" />
          <div className="space-y-2 text-sm">
            <div className="flex justify-between mt-3">
              <span className="px-10">Received Amount</span>
              <Input
                type="number"
                value={inputValue === 0 ? "" : inputValue}
                onChange={handleclick}
                // onBlur={handleBlur}
                placeholder="0"
                inputProps={{ step: "any", }}
                disableUnderline
                className="w-[100px] h-9 text-right text-gray-800 placeholder:text-gray-700 border border-gray-400 rounded-md px-2 py-1 text-base" />
            </div>

            <div className="flex justify-between">
              <span className="px-10">Remaining Amount</span>
              <span className="font-medium">{formattedBalance} QAR</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <Divider className="my-2" />
        <div className="flex justify-end gap-4 mt-5 mb-0">
          <CommonButtons className="w-36" label="Cancel" onClick={() => setglowery(false)} variant="outlineDanger" />
          <CommonButtons className="w-36" label={isLoading ? "Processing..." : "Complete"} onClick={handleComplete} disabled={desableBtn} />
        </div>
      </div>
    </ModalWrapper>
  );
};

export default CashPaymentSummaryModal;
